import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import { Search, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

function VerifyCredential() {
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    setHasSearched(true);
    // In a real app, this would query the Supabase backend.
    // For this frontend-only demo, we show the "No Results" state or mock state.
  };

  return (
    <>

      <div className="max-w-4xl mx-auto py-12 px-6">
        <h1 className="text-3xl font-bold text-[#102A43] mb-8 border-b border-gray-200 pb-4">Verify Credential</h1>

        <div className="space-y-12">
          
          {/* Search Section */}
          <section className="bg-[#F9FAFB] p-8 border border-gray-200 rounded-sm text-center">
            <h2 className="text-xl font-bold text-[#102A43] mb-4">Search Public Registry</h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Enter an organization name or Verification ID to confirm their current standing with the AI Accountability Standards Council.
            </p>
            
            <form onSubmit={handleSearch} className="max-w-lg mx-auto flex gap-2">
              <input 
                type="text" 
                placeholder="Organization Name or ID (e.g. AASC-2026-X9Y2)" 
                className="flex-grow p-3 border border-[#E2E8F0] rounded-sm text-[#102A43] focus:ring-2 focus:ring-blue-900 outline-none bg-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button type="submit" className="bg-[#2F80ED] hover:bg-[#2563eb] text-white font-bold px-6 rounded-sm flex items-center gap-2">
                <Search size={18} /> Verify
              </Button>
            </form>
          </section>

          {/* Results Display (Mock) */}
          {hasSearched && (
            <section className="border border-gray-200 rounded-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
               <div className="bg-gray-100 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                 <h3 className="font-bold text-[#64748B]">Search Results</h3>
                 <span className="text-xs text-gray-500">Live Registry Data</span>
               </div>
               <div className="p-8 text-center">
                  <div className="inline-block p-4 bg-[#F9FAFB] rounded-full mb-4">
                    <Search className="text-gray-400" size={32} />
                  </div>
                  <h3 className="text-lg font-bold text-[#102A43] mb-2">No Records Found</h3>
                  <p className="text-gray-600 mb-6">
                    We could not find an active credential matching "{searchTerm}".
                  </p>
                  <div className="text-sm text-gray-500 bg-yellow-50 p-4 rounded-sm border border-yellow-100 inline-block text-left max-w-lg">
                    <p className="font-bold text-yellow-800 mb-1">Why might this happen?</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>The organization may not be declared.</li>
                      <li>The status may be Expired or Revoked.</li>
                      <li>The organization name may be spelled differently.</li>
                    </ul>
                  </div>
               </div>
            </section>
          )}

          {/* Disclaimers */}
          <section className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 p-6 rounded-sm shadow-sm">
              <div className="flex items-center gap-2 mb-3 text-[#2F80ED]">
                <ShieldCheck size={24} />
                <h3 className="font-bold">What Verification Confirms</h3>
              </div>
              <p className="text-sm text-[#64748B] leading-relaxed">
                A "Valid" result confirms only that the entity holds the stated status (declared or Accountability Declared) at this specific moment. It confirms the entity has met the structural documentation requirements of the AASC Standards Framework.
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-6 rounded-sm shadow-sm">
              <div className="flex items-center gap-2 mb-3 text-red-700">
                <AlertCircle size={24} />
                <h3 className="font-bold">What It Does NOT Confirm</h3>
              </div>
              <p className="text-sm text-[#64748B] leading-relaxed">
                Verification is NOT a guarantee of system safety, performance accuracy, or reliability. It is NOT an endorsement of the operator's business practices, nor does it confirm compliance with regional laws or regulations (e.g., EU AI Act, GDPR).
              </p>
            </div>
          </section>

          <div className="text-center pt-8 border-t border-gray-200">
            <Link to="/registry-status-definitions" className="text-[#2F80ED] font-bold hover:underline">
              View Registry Status Definitions &rarr;
            </Link>
          </div>

        </div>
      </div>
    </>
  );
}

export default VerifyCredential;